import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import type { Channel } from 'pusher-js';
import { AgentEvents } from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import type { WaBriefingList, WaBriefingTodo } from '../../types/waBriefing';
import MorningList, {
  type MorningListData,
  type MorningListPost,
} from './MorningList';

interface ContentResponse {
  data?: {
    date: string;
    assistantName: string | null;
    todos: WaBriefingTodo[];
    lists: WaBriefingList[];
  };
}

/** The Morning List on the signed-in home, read and changed as the user. */
const HomeMorningList: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const linkedListId = searchParams.get('list');
  const [data, setData] = useState<MorningListData | null>(null);
  const [failed, setFailed] = useState(false);
  const reload = useRef<() => void>(() => undefined);
  const pusher = usePusher(true) as { userChannel?: Channel | null } | null;
  const userChannel = pusher?.userChannel ?? null;

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<ContentResponse>('/morning-list/content', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!live || !res.data.data) return;
        setData({
          ...res.data.data,
          language: navigator.language || 'en-GB',
        });
      } catch {
        if (live) setFailed(true);
      }
    };
    // Others can tick off or re-date shared to-dos; refresh on coming back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    reload.current = () => void load();
    void load();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [getAccessTokenSilently]);

  // A to-do changed elsewhere, or a task thread Serafina runs for one moved on.
  useEffect(() => {
    if (!userChannel) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onChange = () => {
      clearTimeout(timer);
      timer = setTimeout(() => reload.current(), 500);
    };
    userChannel.bind(AgentEvents.USER_TODO_UPDATED, onChange);
    return () => {
      clearTimeout(timer);
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, onChange);
    };
  }, [userChannel]);

  const post: MorningListPost = useCallback(
    async (path, body) => {
      const token = await getAccessTokenSilently();
      const res = await api.post(`/morning-list/${path}`, body, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      return {
        ok: res.status >= 200 && res.status < 300,
        status: res.status,
        payload: res.data ?? null,
      };
    },
    [getAccessTokenSilently],
  );

  const update = useCallback(
    (patch: Partial<MorningListData>) =>
      setData((prev) => (prev ? { ...prev, ...patch } : prev)),
    [],
  );

  if (failed) {
    return (
      <p className='ml__load-error' role='alert'>
        Your list didn&apos;t load. Refresh to try again.
      </p>
    );
  }
  if (!data) return null;
  return (
    <MorningList
      key={linkedListId}
      initialListId={linkedListId}
      data={data}
      update={update}
      post={post}
      embedded={embedded}
    />
  );
};

export default HomeMorningList;
